import { useEffect, useMemo, useState } from "react";
import { api, API, formatApiErrorDetail } from "../lib/api";
import { Plus, Pencil, Trash2, FileText, X, Search, FileDown } from "lucide-react";
import { toast } from "sonner";
import CondominioSearchSelect from "../components/CondominioSearchSelect";
import ExportExcelButton from "../components/ExportExcelButton";

const CATEGORIAS = [
  "PR - Processo de Reabilitação",
  "PRL - Cobertura",
  "PRL - Fachada",
  "PRL - Outros",
  "Concurso",
  "Judicial",
  "Fiscalização e Coordenação de Segurança em Obra",
  "Vistoria",
  "Outros orçamentos",
];
const ESTADOS = ["Agendado", "Em Curso", "Concluído", "Cancelado"];

const EMPTY = {
  condominio_id: "",
  tipo: "Orçamento",
  categoria: CATEGORIAS[0],
  descricao: "",
  data: new Date().toISOString().slice(0, 10),
  valor: 0,
  estado: "Agendado",
  tecnico: "",
};

function eur(v) {
  const n = Number(v);
  if (!isFinite(n)) return "€0,00";
  return n.toLocaleString("pt-PT", { style: "currency", currency: "EUR" });
}

export default function Orcamentos() {
  const [items, setItems] = useState([]);
  const [condominios, setCondominios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [filterCategoria, setFilterCategoria] = useState("");
  const [filterEstado, setFilterEstado] = useState("");

  async function refresh() {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get("/servicos", { params: { tipo: "Orçamento" } }),
        api.get("/condominios"),
      ]);
      setItems(s.data);
      setCondominios(c.data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  const condMap = useMemo(
    () => Object.fromEntries(condominios.map((c) => [c.id, c.nome])),
    [condominios]
  );

  const filtered = items.filter((s) => {
    if (filterCategoria && s.categoria !== filterCategoria) return false;
    if (filterEstado && s.estado !== filterEstado) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (condMap[s.condominio_id] || "").toLowerCase().includes(q) ||
      (s.categoria || "").toLowerCase().includes(q) ||
      (s.descricao || "").toLowerCase().includes(q)
    );
  });

  const totais = useMemo(() => {
    return {
      count: filtered.length,
      valor: filtered.reduce((acc, s) => acc + (Number(s.valor) || 0), 0),
    };
  }, [filtered]);

  function openCreate() {
    setEditing(null);
    setForm({ ...EMPTY, condominio_id: condominios[0]?.id || "" });
    setOpenForm(true);
  }

  function openEdit(item) {
    setEditing(item);
    setForm({
      ...EMPTY,
      ...item,
      categoria: item.categoria || CATEGORIAS[0],
      tipo: "Orçamento",
    });
    setOpenForm(true);
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.condominio_id) return toast.error("Selecione um condomínio");
    setSaving(true);
    try {
      const payload = { ...form, tipo: "Orçamento", valor: Number(form.valor) || 0 };
      if (editing) {
        await api.put(`/servicos/${editing.id}`, payload);
        toast.success("Orçamento atualizado");
      } else {
        await api.post("/servicos", payload);
        toast.success("Orçamento criado");
      }
      setOpenForm(false);
      refresh();
    } catch (er) {
      toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message);
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(item) {
    if (!window.confirm("Eliminar este orçamento?")) return;
    try {
      await api.delete(`/servicos/${item.id}`);
      toast.success("Orçamento eliminado");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function onDownloadPdf(item) {
    try {
      const resp = await api.get(`/servicos/${item.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orcamento_${item.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("PDF gerado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao gerar PDF");
    }
  }

  return (
    <div className="space-y-6" data-testid="orcamentos-page">
      {/* Stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Nº orçamentos visíveis" value={totais.count} data-testid="stat-count" />
        <StatCard label="Valor total" value={eur(totais.valor)} data-testid="stat-valor" />
        <StatCard
          label="Valor médio"
          value={eur(totais.count ? totais.valor / totais.count : 0)}
          data-testid="stat-medio"
        />
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
            <input
              data-testid="orcamentos-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Pesquisar por condomínio, categoria ou descrição…"
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-zinc-200 rounded-sm text-sm font-manrope focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none"
            />
          </div>
          <select
            data-testid="orcamentos-filter-categoria"
            value={filterCategoria}
            onChange={(e) => setFilterCategoria(e.target.value)}
            className="bg-white border border-zinc-200 rounded-sm px-4 py-2.5 font-manrope text-sm text-neutral-900 focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none min-w-[260px]"
          >
            <option value="">Todas as categorias</option>
            {CATEGORIAS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <select
            data-testid="orcamentos-filter-estado"
            value={filterEstado}
            onChange={(e) => setFilterEstado(e.target.value)}
            className="bg-white border border-zinc-200 rounded-sm px-4 py-2.5 font-manrope text-sm text-neutral-900 focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none"
          >
            <option value="">Todos os estados</option>
            {ESTADOS.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <ExportExcelButton
            endpoint="/servicos/export/xlsx"
            filename="orcamentos.xlsx"
            testid="export-orcamentos-btn"
          />
          <button
            data-testid="orcamentos-new-btn"
            onClick={openCreate}
            disabled={condominios.length === 0}
            className="bg-[#0047FF] text-white hover:bg-[#003BCC] rounded-sm px-5 py-2.5 font-manrope font-semibold text-sm transition-colors flex items-center gap-2 disabled:opacity-60"
          >
            <Plus className="w-4 h-4" strokeWidth={2} />
            Novo orçamento
          </button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm font-manrope text-neutral-500">A carregar…</div>
        ) : filtered.length === 0 ? (
          <div className="p-14 text-center">
            <FileText className="w-8 h-8 text-neutral-300 mx-auto mb-4" strokeWidth={1.5} />
            <div className="text-sm font-manrope text-neutral-500">
              {condominios.length === 0
                ? "Registe primeiro um condomínio para criar orçamentos."
                : items.length === 0
                ? "Nenhum orçamento emitido."
                : "Sem resultados para os filtros aplicados."}
            </div>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-zinc-50/50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold">
                <th className="py-3 px-6 text-left">Data</th>
                <th className="py-3 px-6 text-left">Condomínio</th>
                <th className="py-3 px-6 text-left">Categoria</th>
                <th className="py-3 px-6 text-left">Descrição</th>
                <th className="py-3 px-6 text-right">Valor</th>
                <th className="py-3 px-6 text-left">Estado</th>
                <th className="py-3 px-6 text-left">Registado por</th>
                <th className="py-3 px-6 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr
                  key={s.id}
                  className="border-b border-zinc-100 hover:bg-zinc-50/60"
                  data-testid={`orcamento-row-${s.id}`}
                >
                  <td className="py-4 px-6 font-manrope text-sm text-neutral-700 font-mono">
                    {s.data}
                  </td>
                  <td className="py-4 px-6 font-manrope text-sm text-neutral-950 font-semibold">
                    {condMap[s.condominio_id] || "—"}
                  </td>
                  <td className="py-4 px-6 font-manrope text-sm text-neutral-700">
                    {s.categoria || <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="py-4 px-6 font-manrope text-sm text-neutral-600 max-w-xs truncate">
                    {s.descricao}
                  </td>
                  <td className="py-4 px-6 font-manrope text-sm text-neutral-950 font-semibold text-right">
                    {eur(s.valor)}
                  </td>
                  <td className="py-4 px-6">
                    <EstadoChip s={s.estado} />
                  </td>
                  <td className="py-4 px-6 font-manrope text-sm text-neutral-700">
                    {s.created_by_name || s.created_by_email ? (
                      <div>
                        <div>{s.created_by_name || "—"}</div>
                        {s.created_by_email && (
                          <div className="text-xs text-neutral-500 font-mono">{s.created_by_email}</div>
                        )}
                      </div>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="py-4 px-6 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        data-testid={`pdf-orcamento-${s.id}`}
                        onClick={() => onDownloadPdf(s)}
                        className="p-2 text-neutral-500 hover:text-[#15803D] hover:bg-[#15803D]/5 rounded-sm"
                        title="Descarregar PDF"
                      >
                        <FileDown className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                      <button
                        data-testid={`edit-orcamento-${s.id}`}
                        onClick={() => openEdit(s)}
                        className="p-2 text-neutral-500 hover:text-[#0047FF] hover:bg-[#0047FF]/5 rounded-sm"
                      >
                        <Pencil className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                      <button
                        data-testid={`delete-orcamento-${s.id}`}
                        onClick={() => onDelete(s)}
                        className="p-2 text-neutral-500 hover:text-[#FF4500] hover:bg-[#FF4500]/5 rounded-sm"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {openForm && (
        <div
          className="fixed inset-0 bg-neutral-950/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setOpenForm(false)}
        >
          <div
            className="bg-white rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="orcamentos-modal"
          >
            <div className="sticky top-0 bg-white border-b border-zinc-200 px-8 py-5 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-400 font-chivo font-bold mb-1">
                  {editing ? "Editar orçamento" : "Novo orçamento"}
                </div>
                <h2 className="text-xl font-chivo font-black text-neutral-950 tracking-tight">
                  {editing ? `Editar · ${editing.categoria || "Orçamento"}` : "Gerar novo orçamento"}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setOpenForm(false)}
                data-testid="orcamentos-close-modal"
                className="p-2 text-neutral-400 hover:text-neutral-900 rounded-sm hover:bg-zinc-100"
              >
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>
            <form onSubmit={onSubmit} className="p-8 space-y-5" data-testid="orcamentos-form">
              <CondominioSearchSelect
                value={form.condominio_id}
                onChange={(v) => setForm({ ...form, condominio_id: v })}
                condominios={condominios}
                testid="input-cond"
              />
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold block mb-2">
                  Categoria *
                </label>
                <select
                  data-testid="input-categoria"
                  value={form.categoria}
                  required
                  onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                  className="w-full bg-white border border-zinc-200 rounded-sm px-4 py-2.5 font-manrope text-sm focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none"
                >
                  {CATEGORIAS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold block mb-2">
                    Data *
                  </label>
                  <input
                    data-testid="input-data"
                    type="date"
                    required
                    value={form.data}
                    onChange={(e) => setForm({ ...form, data: e.target.value })}
                    className="w-full bg-white border border-zinc-200 rounded-sm px-4 py-2.5 font-manrope text-sm focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold block mb-2">
                    Valor (€) *
                  </label>
                  <input
                    data-testid="input-valor"
                    type="number"
                    required
                    step="0.01"
                    value={form.valor}
                    onChange={(e) => setForm({ ...form, valor: e.target.value })}
                    className="w-full bg-white border border-zinc-200 rounded-sm px-4 py-2.5 font-manrope text-sm focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold block mb-2">
                    Estado *
                  </label>
                  <select
                    data-testid="input-estado"
                    value={form.estado}
                    onChange={(e) => setForm({ ...form, estado: e.target.value })}
                    className="w-full bg-white border border-zinc-200 rounded-sm px-4 py-2.5 font-manrope text-sm focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none"
                  >
                    {ESTADOS.map((e) => (
                      <option key={e} value={e}>
                        {e}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold block mb-2">
                  Técnico responsável
                </label>
                <input
                  data-testid="input-tecnico"
                  value={form.tecnico || ""}
                  placeholder="Ex.: Eng. António Silva"
                  onChange={(e) => setForm({ ...form, tecnico: e.target.value })}
                  className="w-full bg-white border border-zinc-200 rounded-sm px-4 py-2.5 font-manrope text-sm focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold block mb-2">
                  Descrição
                </label>
                <textarea
                  data-testid="input-descricao"
                  value={form.descricao || ""}
                  onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                  rows={4}
                  placeholder="Âmbito, condições e notas do orçamento"
                  className="w-full bg-white border border-zinc-200 rounded-sm px-4 py-2.5 font-manrope text-sm focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none resize-none"
                />
              </div>
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-zinc-200">
                <button
                  type="button"
                  data-testid="cancel-btn"
                  onClick={() => setOpenForm(false)}
                  className="bg-white text-neutral-900 border border-zinc-200 hover:bg-zinc-50 rounded-sm px-5 py-2.5 font-manrope font-medium text-sm"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  data-testid="save-btn"
                  disabled={saving}
                  className="bg-[#0047FF] text-white hover:bg-[#003BCC] rounded-sm px-6 py-2.5 font-manrope font-semibold text-sm disabled:opacity-60"
                >
                  {saving ? "A guardar…" : editing ? "Guardar alterações" : "Gerar orçamento"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, ...rest }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-5" {...rest}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold mb-2">
        {label}
      </div>
      <div className="text-2xl font-chivo font-black text-neutral-950 leading-none">{value}</div>
    </div>
  );
}

function EstadoChip({ s }) {
  const map = {
    Agendado: "bg-[#0047FF]/10 text-[#0047FF] border-[#0047FF]/20",
    "Em Curso": "bg-[#F59E0B]/10 text-[#B45309] border-[#F59E0B]/30",
    Concluído: "bg-[#16A34A]/10 text-[#15803D] border-[#16A34A]/30",
    Cancelado: "bg-zinc-100 text-neutral-600 border-zinc-200",
  };
  return (
    <span
      className={`inline-block text-[10px] font-chivo font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm border ${
        map[s] || map.Cancelado
      }`}
    >
      {s}
    </span>
  );
}
